import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "react-toastify";
import { cn } from "lib/utils";

/**
 * CopyButton
 * Reusable dynamic copy button supporting both icon-only and text button variants.
 *
 * @param {string} text - The text to copy to clipboard.
 * @param {string} toastMessage - Success toast notification message.
 * @param {boolean} showToast - Whether to trigger toast notification (default true).
 * @param {number} size - Icon size in px (default 15).
 * @param {boolean} showFeedback - Whether to show checkmark feedback icon temporarily (default true).
 * @param {React.ReactNode} children - Optional label text to render alongside the icon.
 * @param {string} className - Optional container classes.
 * @param {string} iconClassName - Optional classes for the icon.
 * @param {Function} onClick - Optional additional click handler.
 */
const CopyButton = ({
  text = "",
  toastMessage = "Copied to clipboard",
  showToast = true,
  size = 15,
  showFeedback = true,
  children,
  className = "",
  iconClassName = "",
  title = "Copy",
  onClick,
  ...props
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e?.stopPropagation?.();
    if (!text && text !== 0) return;

    try {
      await navigator.clipboard.writeText(String(text));
      if (showToast && toastMessage) {
        toast.success(toastMessage);
      }
      if (showFeedback) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
      onClick?.(e);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy to clipboard");
    }
  };

  const IconComponent = copied ? Check : Copy;

  // Text / Action Button Variant (when children is provided)
  if (children) {
    return (
      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          "inline-flex items-center gap-2 text-sm font-medium text-main-dark hover:text-black transition-colors cursor-pointer bg-transparent border-0 p-0",
          className
        )}
        title={title}
        {...props}
      >
        <IconComponent
          size={size || 18}
          className={cn(
            copied ? "text-success-icon" : "text-main-dark",
            "transition-colors shrink-0",
            iconClassName
          )}
        />
        <span>{children}</span>
      </button>
    );
  }

  // Icon-only Variant (default)
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "text-icon-muted hover:text-slate-text p-0.5 transition-colors cursor-pointer bg-transparent border-0 inline-flex items-center justify-center rounded-sm",
        className
      )}
      title={title}
      aria-label={title}
      {...props}
    >
      <IconComponent
        size={size}
        className={cn(
          copied ? "text-success-icon" : "text-icon-muted hover:text-slate-text",
          "transition-colors shrink-0",
          iconClassName
        )}
      />
    </button>
  );
};

export default CopyButton;
